import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiPlus, FiUsers, FiMail } from 'react-icons/fi';
import { Link, useLocation } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { VerificationRequiredModal } from '../components/mail/Modals';
import { useGetInboxBroadcasts } from '../api/mailing-lists';
import { parseApiError } from '../utils/apiError';

export const Route = createFileRoute('/mail')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: MailLayout,
});

function MailLayout() {
  const location = useLocation();
  const isContacts = location.pathname.startsWith('/mail/contacts');
  const isCompose = location.pathname === '/mail/compose';
  const isBaseMailPath = location.pathname === '/mail' || location.pathname === '/mail/';

  // Sidebar Tab and Pagination states
  const [sidebarTab, setSidebarTab] = useState<'sent' | 'draft'>('sent');
  const [page, setPage] = useState(1);

  // Resizable sidebar width (desktop only)
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('mail-sidebar-width') : null;
    return saved ? parseInt(saved, 10) : 300;
  });
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(300);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = sidebarWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [sidebarWidth],
  );

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(Math.max(startWidth.current + delta, 200), 500);
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('mail-sidebar-width', String(sidebarWidth));
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarWidth]);

  // Fetch broadcasts from API
  const {
    data: inboxData,
    isLoading,
    isError,
    error,
  } = useGetInboxBroadcasts(sidebarTab, page, 50);
  const broadcasts = inboxData?.results ?? [];
  const sentCount = inboxData?.sent_count ?? 0;
  const draftCount = inboxData?.draft_count ?? 0;

  // A 403 on the inbox means the account isn't allowed to use mail yet (verification required)
  const verificationError = useMemo(() => {
    if (!error) return null;
    const parsed = parseApiError(error);
    return parsed.fromApi && parsed.status === 403 ? parsed.message : null;
  }, [error]);

  // Whether we're still in the initial loading state (before any result)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  React.useEffect(() => {
    if (!isLoading && !initialLoadComplete) {
      // Small delay so the loading logo doesn't flash for instant responses
      const timer = setTimeout(() => setInitialLoadComplete(true), 600);
      return () => clearTimeout(timer);
    }
  }, [isLoading, initialLoadComplete]);

  // Helper: get the active broadcast UUID from the URL
  const activeBroadcastUuid = (() => {
    const match = location.pathname.match(/\/mail\/broadcasts\/([^/]+)/);
    return match ? match[1] : null;
  })();

  // Helper: format relative date groups
  const getRelativeDateGroup = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    const dateUtc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    const diffDays = Math.floor((nowUtc - dateUtc) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays === 2) return '2 days ago';
    if (diffDays === 3) return '3 days ago';
    if (diffDays === 4) return '4 days ago';
    if (diffDays === 5) return '5 days ago';
    if (diffDays === 6) return '6 days ago';
    if (diffDays <= 14) return '1 week ago';
    if (diffDays <= 21) return '2 weeks ago';
    if (diffDays <= 30) return '3 weeks ago';
    return 'Older';
  };

  // Helper: format email time (e.g. "02:23pm")
  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date
        .toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
        .toLowerCase();
    } catch {
      return '';
    }
  };

  // Group broadcasts by relative date
  const groupOrder: string[] = [];
  const groupedBroadcasts: Record<string, typeof broadcasts> = {};

  broadcasts.forEach((b) => {
    const dateStr = b.sent_at || b.created_at;
    const group = getRelativeDateGroup(dateStr);
    if (!groupOrder.includes(group)) {
      groupOrder.push(group);
    }
    if (!groupedBroadcasts[group]) {
      groupedBroadcasts[group] = [];
    }
    groupedBroadcasts[group].push(b);
  });

  // Show loading logo until the initial query completes
  if (!initialLoadComplete) {
    return <LoadingLogo />;
  }

  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      <Helmet>
        <title>Mail | Musicade</title>
      </Helmet>

      <Header />

      {/* Verification required modal — shown on top of the mail UI */}
      {verificationError && (
        <VerificationRequiredModal
          message={verificationError}
          onClose={() => {
            // Navigate away from mail since the user can't use it yet
            window.history.back();
          }}
        />
      )}

      <div className="flex-1 flex flex-col md:flex-row p-0 md:p-2 pt-[var(--header-height,5.5rem)] md:pt-[var(--header-height,5.5rem)] md:pb-2 overflow-hidden">
        {/* Left sidebar */}
        <div
          className={`w-full md:w-[var(--sidebar-width)] flex-shrink-0 flex flex-col flex-1 md:flex-none rounded-none md:rounded-[24px] border-0 md:border border-[#262626] bg-[#0A0A0A] min-h-0 overflow-hidden ${
            isBaseMailPath ? 'flex' : 'hidden md:flex'
          }`}
          style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 md:px-5 h-14 md:h-[72px] border-b border-[#262626] flex-shrink-0">
            <Link
              to="/mail"
              className="text-xl font-bold tracking-tight hover:text-gray-300 transition-colors"
            >
              Mail
            </Link>
            {isCompose ? (
              <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-white/30 text-sm font-medium cursor-default border border-white/5">
                <FiPlus size={14} />
                Compose
              </span>
            ) : (
              <Link
                to="/mail/compose"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-100 transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)]"
              >
                <FiPlus size={14} />
                Compose
              </Link>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-2.5 md:py-3 border-b border-[#262626] flex-shrink-0">
            <button
              onClick={() => {
                setSidebarTab('sent');
                setPage(1);
              }}
              className={`flex items-center gap-1.5 pb-2 text-xs font-semibold border-b-2 transition-colors duration-200 ${
                sidebarTab === 'sent'
                  ? 'text-white border-white'
                  : 'text-[#525252] border-transparent hover:text-gray-300'
              }`}
            >
              Sent
              {sentCount > 0 && (
                <span
                  className={`text-[10px] tabular-nums rounded-full px-1.5 py-0.5 font-semibold ${
                    sidebarTab === 'sent'
                      ? 'bg-white text-black font-bold'
                      : 'bg-white/10 text-[#C0C0C0]'
                  }`}
                >
                  {sentCount}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setSidebarTab('draft');
                setPage(1);
              }}
              className={`flex items-center gap-1.5 pb-2 text-xs font-semibold border-b-2 transition-colors duration-200 ${
                sidebarTab === 'draft'
                  ? 'text-white border-white'
                  : 'text-[#525252] border-transparent hover:text-gray-300'
              }`}
            >
              Draft
              {draftCount > 0 && (
                <span
                  className={`text-[10px] tabular-nums rounded-full px-1.5 py-0.5 font-semibold ${
                    sidebarTab === 'draft'
                      ? 'bg-white text-black font-bold'
                      : 'bg-white/10 text-[#C0C0C0]'
                  }`}
                >
                  {draftCount}
                </span>
              )}
            </button>
          </div>

          {/* List of broadcasts */}
          <div className="flex-1 overflow-y-auto no-scrollbar px-2 md:px-3 py-3 md:py-4 min-h-0">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <p className="text-xs text-[#525252]">Loading...</p>
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center py-12">
                <p className="text-xs text-red-400">Failed to load messages</p>
              </div>
            )}

            {!isLoading && !isError && broadcasts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] flex items-center justify-center mb-4 border border-[#333]/40">
                  <FiMail size={20} className="text-[#525252]" />
                </div>
                <p className="text-sm font-semibold text-white mb-1">
                  {sidebarTab === 'sent' ? 'No emails sent' : 'No drafts'}
                </p>
                <p className="text-xs text-[#525252] leading-relaxed max-w-[180px]">
                  {sidebarTab === 'sent'
                    ? 'Emails you send will appear here.'
                    : 'Saved drafts will appear here.'}
                </p>
              </div>
            )}

            {!isLoading &&
              !isError &&
              groupOrder.map((group) => (
                <div key={group} className="mb-4">
                  {/* Date Group Header */}
                  <h4 className="text-[10px] font-semibold text-[#525252] uppercase tracking-wider px-2 mb-2">
                    {group}
                  </h4>

                  {/* Rows inside group */}
                  <div className="space-y-0.5">
                    {groupedBroadcasts[group].map((broadcast) => {
                      const isActive = activeBroadcastUuid === broadcast.uuid;
                      const emails = broadcast.contact_emails ?? [];
                      const recipientLabel =
                        emails.length === 0
                          ? 'No recipients'
                          : emails.length <= 3
                            ? emails.join(', ')
                            : `${emails.slice(0, 3).join(', ')} +${emails.length - 3} more`;

                      const dateStr = broadcast.sent_at || broadcast.created_at;
                      const timeLabel = formatTime(dateStr);

                      return (
                        <Link
                          key={broadcast.uuid}
                          to="/mail/broadcasts/$broadcastUuid"
                          params={{ broadcastUuid: broadcast.uuid }}
                          search={{ listUuid: broadcast.mailing_list_uuid }}
                          className={`w-full flex items-start gap-2.5 md:gap-3 p-2.5 md:p-3 rounded-xl md:rounded-2xl transition-all duration-200 text-left group ${
                            isActive
                              ? 'bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]'
                              : 'hover:bg-white/[0.04]'
                          }`}
                        >
                          {/* Icon */}
                          <div
                            className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              isActive
                                ? 'bg-[#2F80ED]/20 border border-[#2F80ED]/30 text-[#2F80ED]'
                                : 'bg-[#161616] border border-[#262626] text-[#737373] group-hover:border-[#333]'
                            }`}
                          >
                            <FiMail size={13} className="md:hidden" />
                            <FiMail size={14} className="hidden md:block" />
                          </div>

                          {/* Text Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span
                                className={`text-xs font-semibold truncate ${
                                  isActive ? 'text-white' : 'text-[#C0C0C0] group-hover:text-white'
                                }`}
                              >
                                {broadcast.subject || '(No subject)'}
                              </span>
                              <span className="text-[10px] text-[#525252] flex-shrink-0">
                                {timeLabel}
                              </span>
                            </div>
                            <p className="text-[11px] text-[#737373] truncate mt-0.5 leading-normal">
                              {recipientLabel}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}

            {/* Sidebar Pagination */}
            {inboxData && inboxData.count > 50 && (
              <div className="flex items-center justify-between px-2 pt-2 border-t border-[#262626]/50">
                <button
                  disabled={!inboxData.previous}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-[10px] text-[#525252] hover:text-white disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-[10px] text-[#525252]">Page {page}</span>
                <button
                  disabled={!inboxData.next}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-[10px] text-[#525252] hover:text-white disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Footer button */}
          <div className="p-3 md:p-4 border-t border-[#262626] flex-shrink-0">
            <Link
              to="/mail/contacts"
              className={`w-full flex items-center justify-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 ${
                isContacts
                  ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.1)]'
                  : 'bg-[#141414] text-[#A3A3A3] hover:text-white hover:bg-[#1e1e1e] border border-[#2a2a2a]'
              }`}
            >
              <FiUsers size={14} className="md:hidden" />
              <FiUsers size={15} className="hidden md:block" />
              Manage Contact List
            </Link>
          </div>
        </div>

        {/* Desktop resize handle */}
        <div
          className="hidden md:block w-1.5 flex-shrink-0 cursor-col-resize hover:bg-[#2F80ED]/30 transition-colors rounded-full my-2"
          onMouseDown={startResize}
        />

        {/* Right panel - renders child route content */}
        <div
          className={`flex-1 min-w-0 ${isBaseMailPath ? 'hidden md:flex md:flex-col' : 'flex flex-col'}`}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}
